import React, { useState, useRef, useEffect } from 'react';
import { 
  Button, 
  Form, 
  Input, 
  Upload, 
  message, 
  Card, 
  Space,
  Modal,
  App
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  PictureOutlined,
  KeyOutlined
} from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const CommentEditor = ({ 
  initialValue = '', 
  initialImages = [], 
  onSave, 
  onCancel 
}) => {
  const { modal } = App.useApp();
  const [form] = Form.useForm();
  const [content, setContent] = useState(initialValue);
  const [files, setFiles] = useState(initialImages.map((url, index) => ({
    uid: `-${index}`,
    name: `image-${index}.png`,
    status: 'done',
    url,
  })));
  const [previewImage, setPreviewImage] = useState('');
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const editorRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      const visualViewport = window.visualViewport;
      
      if (isMobile && visualViewport) {
        const keyboardHeight = window.innerHeight - visualViewport.height;
        setIsKeyboardVisible(keyboardHeight > 100);
      } else {
        setIsKeyboardVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    setPreviewImage(file.url || (file.preview));
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleChange = ({ fileList: newFileList }) => {
    setFiles(newFileList);
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!');
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must smaller than 5MB!');
    }
    return isJpgOrPng && isLt5M;
  };

  const handleSubmit = () => {
    const images = files
      .filter(file => file.status === 'done')
      .map(file => file.url || file.preview);
    
    onSave(content, images);
  };

  const hideKeyboard = () => {
    if (editorRef.current) {
      const editor = editorRef.current.getEditor();
      editor.blur();
    }
    setIsKeyboardVisible(false);
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'link', 'image'
  ];

  return (
    <div 
      ref={containerRef}
      style={{
        height: isKeyboardVisible ? '100vh' : 'auto',
        overflow: 'hidden',
        position: isKeyboardVisible ? 'fixed' : 'relative',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: isKeyboardVisible ? 1000 : 'auto',
        backgroundColor: '#fff'
      }}
    >dxfgdsgdgfdgf
      <Card
        title="Редактор комментария"
        extra={
          <Space>
            {isKeyboardVisible && (
              <Button 
                icon={<KeyOutlined />} 
                onClick={hideKeyboard}
              >
                Скрыть клавиатуру
              </Button>
            )}
            {onCancel && (
              <Button onClick={onCancel}>
                Отмена
              </Button>
            )}
            <Button 
              type="primary" 
              onClick={handleSubmit}
            >
              Сохранить
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Текст комментария">
            <ReactQuill
              ref={editorRef}
              theme="snow"
              value={content}
              onChange={setContent}
              modules={modules}
              formats={formats}
              style={{ 
                height: isKeyboardVisible ? '60vh' : 300,
                marginBottom: 16 
              }}
            />
          </Form.Item>
          
          <Form.Item label="Изображения">
            <Upload
              listType="picture-card"
              fileList={files}
              onChange={handleChange}
              onPreview={handlePreview}
              beforeUpload={beforeUpload}
              accept="image/*"
              multiple
            >
              {files.length < 5 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Загрузить</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Card>

      {previewImage && (
        <Modal
          open={!!previewImage}
          title="Просмотр изображения"
          footer={null}
          onCancel={() => setPreviewImage('')}
        >
          <img alt="preview" style={{ width: '100%' }} src={previewImage} />
        </Modal>
      )}
    </div>
  );
};

export default CommentEditor;